import { PRODUCT_DISPLAYS, ProductDisplay, ProductFilterTag, ProductListingConfig } from "./abProducts.types";

type AttributeReader = Pick<Element, "getAttribute">;

export class ProductListingConfigurationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProductListingConfigurationError";
  }
}

export interface ProductSliderLayoutConfig {
  layout: string;
  breakpoints: readonly (readonly [number, "vertical" | "horizontal"])[];
}

export const parseProductSliderLayoutConfig = (element: AttributeReader): ProductSliderLayoutConfig => {
  const layout = element.getAttribute("data-layout") || "horizontal";
  const raw = element.getAttribute("data-card-breakpoints");
  if (raw === null) return { layout, breakpoints: [] };

  const invalid = () => new ProductListingConfigurationError(
    'data-card-breakpoints must be a JSON object mapping non-negative integer pixel widths to "vertical" or "horizontal".',
  );
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw invalid();
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) throw invalid();
  const breakpoints = Object.entries(parsed).map(([key, value]): readonly [number, "vertical" | "horizontal"] => {
    if (!/^(0|[1-9]\d*)$/.test(key) || !Number.isSafeInteger(Number(key)) ||
      (value !== "vertical" && value !== "horizontal")) throw invalid();
    return [Number(key), value];
  });
  return { layout, breakpoints };
};

export const parseProductFilterOpen = (element: AttributeReader): boolean => {
  const value = element.getAttribute("open");
  if (value === null || value === "true") return true;
  if (value === "false") return false;
  throw new ProductListingConfigurationError('Filter open must be "true" or "false".');
};

export const parseProductListingConfig = (
  element: AttributeReader,
  filters: readonly ProductFilterTag[] = [],
): ProductListingConfig => {
  for (const legacy of ["data-collection-type", "data-collection-id", "data-constraints", "data-filters", "data-variant-scope", "data-interstitial-id", "data-interstitial-after-row"]) {
    if (element.getAttribute(legacy) !== null) {
      throw new ProductListingConfigurationError(`${legacy} is no longer supported.`);
    }
  }
  const source = element.getAttribute("data-source");
  if (!source?.length) throw new ProductListingConfigurationError("data-source is required.");
  const displayValue = element.getAttribute("data-display");
  const display = displayValue === null ? "grid" : displayValue as ProductDisplay;
  if (!(PRODUCT_DISPLAYS as readonly string[]).includes(display)) {
    throw new ProductListingConfigurationError("data-display must be grid or slider.");
  }
  if (new Set(filters).size !== filters.length) {
    throw new ProductListingConfigurationError("A filter component can appear only once.");
  }
  if (display === "slider" && filters.length) {
    throw new ProductListingConfigurationError("Filter components are supported only in a grid.");
  }
  const include = element.getAttribute("data-include");
  if (include !== null && include !== "guide-details") {
    throw new ProductListingConfigurationError("Unsupported product include.");
  }
  return {
    source,
    ...(include ? { include: include as "guide-details" } : {}),
    composition: { productIds: (element.getAttribute("data-product-ids") ?? "").split(",").map((id) => id.trim()).filter(Boolean) },
    display,
    filters,
  };
};

export const parseProductListingInterstitialRow = (
  rows: readonly (string | null)[],
  display: ProductDisplay,
): number | null => {
  if (!rows.length) return null;
  if (rows.length > 1) throw new ProductListingConfigurationError("Only one interstitial template is supported.");
  if (display !== "grid") throw new ProductListingConfigurationError("An interstitial template is supported only in a grid.");
  const value = rows[0];
  if (value === null || !/^[1-9]\d*$/.test(value) || !Number.isSafeInteger(Number(value))) {
    throw new ProductListingConfigurationError("data-interstitial-after-row must be a positive integer.");
  }
  return Number(value);
};
